import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import type {LinkProps} from "@mui/material/Link";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import {useContext, type ElementType, type ReactNode, type Ref} from "react";

import {projectCardClasses as classes} from "./classes.js";
import {ProjectCardExpandCollapse} from "./ProjectCardExpandCollapse.js";
import {ProjectCardExpansionContext} from "./ProjectCardExpansionContext.js";

/**
 * The card's thumbnail area. Render it (or omit it entirely) from the host
 * app so the card collapses to its compact layout when there is no thumbnail.
 */
export function ProjectCardThumbnailArea({children}: {children: ReactNode}) {
    return <Box className={classes.thumbnailWrapper}>{children}</Box>;
}

export type ProjectCardTitleProps<C extends ElementType = "a"> = LinkProps<C, {component?: C}> & {
    children: ReactNode;
    previewHeight?: number;
};

/**
 * The card's linked name. Pass routing props for the host app's link
 * implementation (`href` for a plain anchor, `component={RouterLink} to=...`
 * for the client app).
 */
export function ProjectCardTitle<C extends ElementType = "a">(props: ProjectCardTitleProps<C>) {
    const {children, previewHeight, color = "textPrimary", sx = {display: "inline"}, ...linkProps} = props;
    const {expanded, setTitleTruncated, titleTruncated} = useContext(ProjectCardExpansionContext);
    const title = (
        <Typography color="textPrimary" variant="h6" component="h3" className={classes.name}>
            {typeof children === "string" && previewHeight ? (
                <ProjectCardExpandCollapse
                    text={children}
                    previewHeight={previewHeight}
                    expanded={expanded}
                    showToggle={false}
                    onTruncatedChange={setTitleTruncated}
                    renderText={(text) => (
                        <Link color={color} sx={sx} {...linkProps}>
                            {text}
                        </Link>
                    )}
                />
            ) : (
                children
            )}
        </Typography>
    );

    if (typeof children === "string" && previewHeight) {
        return <Tooltip title={titleTruncated && !expanded ? children : ""}>{title}</Tooltip>;
    }

    return (
        <Link color={color} sx={sx} {...linkProps}>
            {title}
        </Link>
    );
}

export function ProjectCardDescriptionPreview({
    text,
    previewHeight,
    expandText,
    collapseText,
}: {
    text: string;
    previewHeight: number;
    expandText?: string;
    collapseText?: string;
}) {
    const {expanded, setExpanded, titleTruncated} = useContext(ProjectCardExpansionContext);
    return (
        <ProjectCardDescription>
            <ProjectCardExpandCollapse
                text={text}
                previewHeight={previewHeight}
                expanded={expanded}
                onExpandedChange={setExpanded}
                forceToggle={titleTruncated || (text === "" && expanded)}
                expandText={expandText}
                collapseText={collapseText}
            />
        </ProjectCardDescription>
    );
}

export function ProjectCardDescription({children}: {children: ReactNode}) {
    return (
        <Typography component="div" variant="body2" className={classes.description}>
            {children}
        </Typography>
    );
}

export function ProjectCardAuthorRow({children}: {children: ReactNode}) {
    return <Box className={classes.authorRow}>{children}</Box>;
}

export function ProjectCardAuthorSeparator() {
    return (
        <Typography variant="body2" color="textSecondary" className={classes.authorSeparator}>
            &middot;
        </Typography>
    );
}

export interface ProjectCardMetricRowProps {
    children: ReactNode;
    /** Clamp the row to a single line (overflowing metrics hidden). */
    collapsed?: boolean;
    innerRef?: Ref<HTMLDivElement>;
    /** Rendered outside the (clamped) inner row, e.g. a show-more button. */
    trailing?: ReactNode;
}

export function ProjectCardMetricRow({children, collapsed, innerRef, trailing}: ProjectCardMetricRowProps) {
    const innerClassName = collapsed
        ? `${classes.metricRowInner} ${classes.metricRowCollapsed}`
        : classes.metricRowInner;
    return (
        <div className={classes.metricRow}>
            <div className={innerClassName} ref={innerRef}>
                {children}
            </div>
            {trailing}
        </div>
    );
}
